import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import { searchHighCourt } from "../../services/escrApi";

import SelectMenu from "../../components/crm/SelectMenu";
const SEARCH_OPTS = [
  { value: "PHRASE", label: "Phrase(s)" },
  { value: "ANY", label: "Any Words" },
  { value: "ALL", label: "All Words" },
];

const SEARCH_TIPS = [
  "Enter keywords, acts or any free text",
  "Phrase(s) matches the exact wording",
  "Searches High Court judgments & orders",
];

export default function ESCRSearch() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [searchOpt, setSearchOpt] = useState("PHRASE");
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!keyword.trim()) return toast.error("Please enter a keyword");

    setLoading(true);
    try {
      const data = await searchHighCourt({ keyword: keyword.trim(), searchType: searchOpt });
      if (!data.results.length) {
        toast("No judgments found for this keyword.", { icon: "ℹ️" });
        setLoading(false);
        return;
      }
      navigate("/portal/escr/judgmentList", {
        state: { search: { keyword: keyword.trim(), searchOpt }, data },
      });
    } catch (err) {
      toast.error(err.message || "Search failed. Please try again.");
      setLoading(false);
    }
  };

  const handleReset = () => {
    setKeyword("");
    setSearchOpt("PHRASE");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">
            Find Cases
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">eSCR Judgment &amp; Order</span>
        </div>

        {/* Stepper */}
        <div className="mt-4">
          <FindCaseStepper current={2} />
        </div>

        {/* Form Card — full width */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <form onSubmit={handleSearch}>
            <div className="p-5 sm:p-6">
              <h2 className="text-sm font-semibold text-slate-800">ESCR : Search Judgments</h2>

              <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Court" required={false}>
                  <SelectShell>
                    <SelectMenu chevron={false} className={selectStyles} value="High Court" disabled>
                      <option>High Court</option>
                    </SelectMenu>
                  </SelectShell>
                </Field>

                <Field label="Keyword" className="lg:col-span-2">
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Enter Keyword"
                    className={inputStyles}
                    autoFocus
                  />
                </Field>

                <Field label="Match" required={false}>
                  <div className="flex min-h-[2.5rem] flex-wrap items-center gap-x-4 gap-y-1">
                    {SEARCH_OPTS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-center text-sm text-slate-700"
                      >
                        <input
                          type="radio"
                          name="searchOpt"
                          value={opt.value}
                          checked={searchOpt === opt.value}
                          onChange={() => setSearchOpt(opt.value)}
                          className="mr-1.5 accent-yellow-500"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
            </div>

            {/* Footer — tips + actions */}
            <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {SEARCH_TIPS.map((tip) => (
                  <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                    {tip}
                  </li>
                ))}
              </ul>
              <div className="flex shrink-0 gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                  disabled={loading}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
